/**
 *
 * Copyright © 2017 Easymeeting AS. All rights reserved.
 *
 * This software is produced by Easymeeting AS.
 * Unauthorized redistribution, reproduction, or usage of
 * this software in whole or in part without the express
 * written consent of Easymeeting AS is strictly prohibited.
 */

export function bindNativeEvents (eventBus) {
  return {
    /**
     * setDeviceInfo
     * Inform device details to the webview to store them in DB while user logging in for push notification and remember user token.
     *
     * $_APP.setDeviceInfo({
     *   app: <appname>
     *   appVersion: <app version>
     *   deviceid: <device-unique-id>
     *   deviceName: <device name>
     *   modelNo: <model number>
     *   os: <ios/android/linux/win/mac>
     *   osVersion: <os version>
     *   pushToken: <device pushnotification token>
     * });
     */
    setDeviceInfo (deviceInfo) {
      eventBus.$emit('native-setDeviceInfo', deviceInfo);
      return true;
    },

    /**
     * updateDeviceInfo
     * Update the device info if it changes.
     *
     * $_APP.updateDeviceInfo({
     *   app: <appname>                                 // optional
     *   appVersion: <app version>                      // optional
     *   deviceid: <device-unique-id>                   // optional
     *   deviceName: <device name>                      // optional
     *   modelNo: <model number>                        // optional
     *   os: <ios/android/linux/win/mac>                // optional
     *   osVersion: <os version>                        // optional
     *   pushToken: <device pushnotification token>     // optional
     * });
     *
     * Note:- No need to give all the options, only need the new updated values
     */
    updateDeviceInfo (deviceInfo) {
      eventBus.$emit('native-updateDeviceInfo', deviceInfo);
      return true;
    },

    /**
     * networkStatus
     * Periodically check network status and inform it to webview.
     *
     * $_APP.networkStatus(<true/false>);
     *    true    - If online
     *    false   - If online
     */
    networkStatus (status) {
      if (typeof status === 'string' || status instanceof String) {
        status = status.toLowerCase() === 'true';
      } else {
        status = !!status;
      }
      eventBus.$emit('native-networkChange', status);
      return true;
    },

    /**
     * standBy
     * Inform the webview to enter or exit standby mode.
     *
     * $_APP.standBy(<true/false>);
     *    true    - To enter standby mode
     *    false   - To exit standby mode
     */
    standBy (mode) {
      eventBus.$emit('native-standBy', mode);
      return true;
    },

    /**
     * onScanSuccess
     * Send the scanned key to webview
     *
     * $_APP.onScanSuccess(<key>);
     *    key    - data scanned from QR Code
     */
    onScanSuccess (key) {
      eventBus.$emit('native-onScanSuccess', key);
      return true;
    },

    /**
     * goBack
     * On device back button click inform webview.
     *
     * $_APP.goBack();
     *
     */
    goBack () {
      eventBus.$emit('native-goBack', {});
      return true;
    },

    /**
     * showToast
     * If native want to show some toast to screen.
     *
     * $_APP.showToast(<message>);
     * message - string message
     *
     */
    showToast (message) {
      eventBus.$emit('showToast', {
        text: message
      });
      return true;
    },

    /**
     * meetingStat
     * Expose meeting statistics to webview periodically on request.
     *
     * $_APP.meetingStat(<data>);
     * data - statistics object
     *
     */
    meetingStat (data) {
      eventBus.$emit('native-meetingStat', data);
      return true;
    },

    /**
     * chatReceived
     * On chat received in meeting.
     *
     * $_APP.chatReceived(<chat data>);
     *
     */
    chatReceived (chat) {
      eventBus.$emit('native-onChatReceived', chat);
      return true;
    },

    /**
     * muteMic
     * On mic mute in meeting.
     *
     * $_APP.muteMic();
     *
     */
    muteMic () {
      eventBus.$emit('native-onMuteMic', {});
      return true;
    },

    /**
     * unmuteMic
     * On mic unmute in meeting.
     *
     * $_APP.unmuteMic();
     *
     */
    unmuteMic () {
      eventBus.$emit('native-onUnmuteMic', {});
      return true;
    },

    /**
     * enableVideo
     * On enable video in meeting.
     *
     * $_APP.enableVideo();
     *
     */
    enableVideo () {
      eventBus.$emit('native-onEnableVideo', {});
      return true;
    },

    /**
     * disableVideo
     * On disable video in meeting.
     *
     * $_APP.disableVideo();
     *
     */
    disableVideo () {
      eventBus.$emit('native-onDisableVideo', {});
      return true;
    },

    /**
     * enableLoudSpeaker
     * On enable loudspeaker in meeting.
     *
     * $_APP.enableLoudSpeaker();
     *
     */
    enableLoudSpeaker () {
      eventBus.$emit('native-onEnableLoudSpeaker', {});
      return true;
    },

    /**
     * disableLoudSpeaker
     * On disable loudspeaker in meeting.
     *
     * $_APP.disableLoudSpeaker();
     *
     */
    disableLoudSpeaker () {
      eventBus.$emit('native-onDisableLoudSpeaker', {});
      return true;
    },

    /**
     * muteSpeaker
     * On speaker mute in meeting.
     *
     * $_APP.muteSpeaker();
     *
     */
    muteSpeaker () {
      eventBus.$emit('native-onMuteSpeaker', {});
      return true;
    },

    /**
     * unmuteSpeaker
     * On speaker unmute in meeting.
     *
     * $_APP.unmuteSpeaker();
     *
     */
    unmuteSpeaker () {
      eventBus.$emit('native-onUnmuteSpeaker', {});
      return true;
    },

    /**
     * onWebrtcEvents
     * On webrtc events inform webview to show the call
     *
     * $_APP.onWebrtcEvents(<call data>);
     *
     */
    onWebrtcEvents (eventData) {
      if (eventData && eventData.eventData) {
        if (
          typeof eventData.eventData === 'string' ||
          eventData.eventData instanceof String
        ) {
          try {
            eventData.eventData = JSON.parse(eventData.eventData);
          } catch (error) {}
        }
      }
      eventBus.$emit('native-onWebrtcEvents', eventData);
      return true;
    },

    /**
     * onPushNotification
     * On push notification inform webview to show the call
     *
     * $_APP.onPushNotification(<call data>);
     *
     */
    onPushNotification (data) {
      eventBus.$emit('native-onPushNotification', data);
      return true;
    },

    /**
     * onOrientationChange
     * On orientation change inform webview to show the call
     *
     * $_APP.onOrientationChange(<data>);
     *
     */
    onOrientationChange (data) {
      eventBus.$emit('native-onOrientationChange', data);
      return true;
    },

    /**
     * volume
     * On volume change inform webview to show the call
     *
     * $_APP.volume(<data>);
     *
     */
    volume (data) {
      eventBus.$emit('native-onVolumeChange', data);
      return true;
    },

    /**
     * isReady
     * to check native that is javascript responding
     *
     * $_APP.isReady();
     *
     */
    isReady () {
      return true;
    },

    /**
     * dialToNumber
     * Dial to a number from native
     *
     * $_APP.dialToNumber(<call data>);
     *
     */
    dialToNumber (data) {
      eventBus.$emit('native-onDialToNumber', data);
      return true;
    },

    /**
     * exitMeeting
     * Exit meeting from native
     *
     * $_APP.exitMeeting(<call data>);
     *
     */
    exitMeeting (data) {
      eventBus.$emit('native-exitMeeting', data);
      return true;
    },

    /**
     * Following are for camera control integration with camera app.
     */

    /**
     * setPairingInfo
     * set the pairing pin code and other info to webview
     *
     * $_APP.setPairingInfo({
     *    pin: <pin generated for connecting controllers>
     *    ...
     * });
     *
     */
    setPairingInfo (data) {
      eventBus.$emit('native-setPairingInfo', data);
      return true;
    },

    /**
     * controlConnected
     * On a new control connection inform webview
     *
     * $_APP.controlConnected({
     *    name: <device name>,
     *    id: <device id>,
     *    ...
     * });
     *
     */
    controlConnected (control) {
      eventBus.$emit('native-controlConnected', control);
      return true;
    },

    /**
     * controlDisconnected
     * On a control disconnection inform webview
     *
     * $_APP.controlDisconnected({
     *    name: <device name>,
     *    id: <device id>,
     *    ...
     * });
     *
     */
    controlDisconnected (control) {
      eventBus.$emit('native-controlDisconnected', control);
      return true;
    },

    /**
     * setControlList
     * On getControlList request set the list of controls connected to webview
     *
     * $_APP.setControlList([{
     *    name: <device name>,
     *    id: <device id>,
     *    ...
     * }, ...]);
     *
     */
    setControlList (controls) {
      eventBus.$emit('native-setControlList', controls);
      return true;
    },

    /**
     * pairingTimeout
     * On pairing timeout inform webview
     *
     * $_APP.pairingTimeout();
     *
     */
    pairingTimeout () {
      eventBus.$emit('native-pairingTimeout');
      return true;
    },

    /**
     * messageFromControl
     * Pass the message from control to camera
     *
     * $_APP.messageFromControl({
     *    type: <message type>,
     *    data: <data>
     * });
     *
     */
    messageFromControl (message, device) {
      eventBus.$emit('native-messageFromControl', { message, device });
      return true;
    },

    /**
     * onPhoneCall
     * On phone call status change
     *
     * $_APP.onPhoneCall({
     *    status: <true/false>
     * });
     *
     */
    onPhoneCall (info) {
      eventBus.$emit('native-onPhoneCall', info);
      return true;
    },

    /**
     * onChatClick
     * On chat click
     *
     * $_APP.onChatClick({
     *    conversationID: <conversation id from notification>
     * });
     *
     */
    onChatClick (info) {
      eventBus.$emit('native-onChatClick', info);
      return true;
    },

    /**
     * onMissedCallClick
     * On missed call click
     *
     * $_APP.onMissedCallClick({
     *    conversationID: <conversation id from notification>
     * });
     *
     */
    onMissedCallClick (info) {
      eventBus.$emit('native-onMissedCallClick', info);
      return true;
    },

    /**
     * authenticateUser
     * authenticate user using authtoken from outh server
     *
     * $_APP.authenticateUser({provider: <oauth provider>, token: <token from oauth server>});
     *
     */
    authenticateUser (data) {
      eventBus.$emit('native-onAuthenticateUser', data);
      return true;
    }
  };
}
